import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Plus, ExternalLink, Edit3, Trash2, Eye, BarChart3, Crown } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ lead_magnets: [], total: 0 });
  const [stats, setStats] = useState({ leads: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [lmRes, leadsRes] = await Promise.all([
        api.get("/lead-magnets"),
        api.get("/leads"),
      ]);
      setData(lmRes.data);
      setStats({ leads: leadsRes.data.total });
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/lead-magnets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const isAdmin = user?.role === "admin";
  const isFree = user?.plan === "free" && !isAdmin;
  const total = data.total;
  const limit = 5;
  const usagePct = isFree ? Math.min(100, (total / limit) * 100) : 0;

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-cap mb-2">// Dashboard</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter" data-testid="dashboard-heading">
            Welcome, {user?.name?.split(" ")[0] || "there"}.
          </h1>
        </div>
        <Link to="/templates" className="btn-brutal" data-testid="new-lead-magnet-btn">
          <Plus size={18} /> New Lead Magnet
        </Link>
      </div>

      {/* Plan status */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="card-brutal" data-testid="stat-leads">
          <div className="label-cap">Total Leads</div>
          <div className="font-display font-black text-4xl mt-2">{stats.leads}</div>
        </div>
        <div className="card-brutal" data-testid="stat-lms">
          <div className="label-cap">Lead Magnets</div>
          <div className="font-display font-black text-4xl mt-2">{total}{isFree && <span className="text-zinc-400 text-2xl"> / {limit}</span>}</div>
          {isFree && (
            <div className="mt-3 h-2 border-2 border-black bg-white">
              <div className="h-full bg-[#FF4F00]" style={{ width: `${usagePct}%` }} />
            </div>
          )}
        </div>
        <div className="card-brutal bg-black text-white border-black" data-testid="stat-plan">
          <div className="label-cap !text-zinc-400">Plan</div>
          <div className="font-display font-black text-3xl mt-2 flex items-center gap-2">
          {renderPlanLabel({ isAdmin, isFree, plan: user?.plan })}
          </div>
          {!isAdmin && (
            <Link to="/pricing" className="inline-block mt-3 text-sm font-bold underline text-[#FF4F00]" data-testid="upgrade-cta">
              {isFree ? "Lock In $89/Year →" : "Manage subscription →"}
            </Link>
          )}
        </div>
      </div>

      {isFree && total >= limit - 1 && (
        <div className="border-2 border-[#FF4F00] bg-[#FFF4EE] p-5 mb-8" data-testid="upgrade-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-cap !text-[#FF4F00] mb-1">Ceiling reached</div>
              <div className="font-bold">You've built {total}/{limit} pages. That's the ceiling for free. The operators who got serious upgraded here — $89/year, 24 cents a day, 30 pages a month, no branding, no limits.</div>
            </div>
            <Link to="/pricing" className="btn-brutal" data-testid="upgrade-banner-btn">Lock In $89/Year</Link>
          </div>
        </div>
      )}

      {/* List */}
      <div className="border-2 border-black bg-white">
        <div className="px-5 py-3 border-b-2 border-black bg-zinc-50 flex items-center justify-between">
          <div className="font-display font-bold text-lg">Your Lead Pages</div>
          <div className="text-sm text-zinc-500">{total} total</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-zinc-500">Loading…</div>
        ) : data.lead_magnets.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-display font-bold text-2xl mb-2">You haven't built your first page yet.</div>
            <p className="text-zinc-600 mb-5">Your list won't grow itself.</p>
            <Link to="/templates" className="btn-brutal" data-testid="empty-create-btn"><Plus size={18} /> Build Your First Page</Link>
          </div>
        ) : (
          <div className="divide-y-2 divide-black">
            {data.lead_magnets.map((lm) => (
              <div key={lm.id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-4 hover:bg-zinc-50" data-testid={`lm-row-${lm.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-bold text-lg">{lm.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    /{lm.slug} · {lm.template_id} · {lm.published ? <span className="text-green-700 font-bold">PUBLISHED</span> : <span>Draft</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => navigate(`/builder/${lm.id}`)} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid={`edit-${lm.id}`}><Edit3 size={14} /> Edit</button>
                  <Link to={`/lm/${lm.slug}`} target="_blank" className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid={`view-${lm.id}`}><Eye size={14} /> View</Link>
                  <Link to={`/leads?lead_magnet_id=${lm.id}`} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid={`leads-${lm.id}`}><BarChart3 size={14} /> Leads</Link>
                  <button onClick={() => handleDelete(lm.id, lm.title)} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid={`delete-${lm.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function renderPlanLabel({ isAdmin, isFree, plan }) {
  if (isAdmin) return (<><Crown className="text-[#FF4F00]" size={28} /> Admin</>);
  if (isFree) return "Free";
  if (plan === "pro_yearly") return "Pro · Yearly";
  return "Pro · Monthly";
}
